import { assertNever } from '../lib/assert-never.js';
import type { ExperienceBlock, ExperienceBlockDraft } from '../schemas/experience.js';

/**
 * The texts of ONE block that may carry a variable (§8.6), in reading order.
 *
 * The runs of a Texte block, the label of a Checkbox - §9.2 says « variables
 * acceptées » there in as many words - and the label of a Lien and the text of a
 * Texte copiable (issue #137), and the question of a Sélection and of a Note
 * (issue #176). Nothing else: the labels of a Bouton and of an
 * Input, an image's caption and a Lien's ADDRESS accept no variable, and a Noyau
 * block carries no text at all (its texts are the versioned texts of the noyaux).
 *
 * ONE list for the two readers that need it, and that is the reason this file
 * exists: `experienceTextSitesOf` walks every block of a content to judge the
 * reach of each token, and `pageDependsOnNoyau` walks one page to find a
 * `@noyau`. Written twice, the day a tenth block accepts a variable one of them
 * would forget it - and a page citing `@noyau` in that block would be served to
 * a person whose sequence was skipped. `assertNever` makes that tenth type a
 * compile error here rather than a text nobody reads.
 */
export function blockVariableTexts(block: ExperienceBlock | ExperienceBlockDraft): string[] {
  switch (block.type) {
    case 'text':
      return block.text.nodes.flatMap((node) =>
        (node.type === 'paragraph' ? [node.children] : node.items.map((item) => item.children))
          .flat()
          .flatMap((inline) => (inline.type === 'text' ? [inline.text] : [])),
      );
    case 'checkbox':
    case 'link':
      return [block.label];
    // The question of a Sélection and of a Note accepts variables like a
    // Checkbox's label (retour-experience.md §4.1, §4.2); the options of a
    // Sélection are plain text and carry none.
    case 'selection':
    case 'note':
      return [block.question];
    case 'copyable':
      return [block.text];
    case 'image':
    case 'sound':
    case 'button':
    case 'input':
    case 'noyau':
      return [];
    default:
      return assertNever(block, 'experience block');
  }
}
